import React, { useState, useEffect, useRef } from 'react';
import { Clock, Users, Zap } from 'lucide-react';

const metrics = [
    {
        id: "uptime",
        number: "99.9",
        label: "Uptime",
        sublabel: "Reliability you can count on",
        icon: <Clock className="w-12 h-12 text-blue-500" />,
        color: "from-blue-600 to-blue-400",
        borderColor: "border-blue-500",
        shadowColor: "shadow-blue-500/20",
        suffix: "%"
    },
    {
        id: "satisfaction",
        number: "97",
        label: "Customer Satisfaction",
        sublabel: "Consistently exceeding expectations",
        icon: <Users className="w-12 h-12 text-indigo-500" />,
        color: "from-indigo-600 to-indigo-400",
        borderColor: "border-indigo-500",
        shadowColor: "shadow-indigo-500/20",
        suffix: "%"
    },
    {
        id: "response",
        number: "20",
        label: "Mins Response Time",
        sublabel: "Swift support when you need it most",
        icon: <Zap className="w-12 h-12 text-amber-500" />,
        color: "from-amber-600 to-amber-400",
        borderColor: "border-amber-500",
        shadowColor: "shadow-amber-500/20",
        suffix: ""
    }
];

const ServiceMetricCard = ({ metric, active }) => {
    const [count, setCount] = useState(0);
    const cardRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    animateNumber();
                }
            },
            { threshold: 0.2 }
        );

        const cardElement = cardRef.current;
        if (cardElement) {
            observer.observe(cardElement);
        }

        return () => {
            if (cardElement) {
                observer.unobserve(cardElement);
            }
        };
    }, []);

    const animateNumber = () => {
        const duration = 2500;
        const endNumber = parseInt(metric.number);
        const startTime = Date.now();

        const updateCount = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            const currentCount = Math.floor(endNumber * progress);

            setCount(currentCount);

            if (progress < 1) {
                requestAnimationFrame(updateCount);
            }
        };

        requestAnimationFrame(updateCount);
    };

    return (
        <div
            ref={cardRef}
            className={`group relative bg-gradient-to-b from-slate-800/80 to-slate-900/80 rounded-xl p-6 border transition-all duration-300 
            ${active ? metric.borderColor + ' ' + metric.shadowColor : 'border-slate-700/50'} 
            hover:${metric.borderColor} hover:${metric.shadowColor} cursor-pointer`}
        >
            {/* Step icon with animated background */}
            <div className={`w-20 h-20 mx-auto mb-6 relative rounded-full border-4 ${active ? metric.borderColor : 'border-slate-700'}`}>
                <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${metric.color} opacity-10 ${active ? 'animate-pulse' : ''}`} style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                    {metric.icon}
                </div>
            </div>

            <div className="text-center mb-4">
                <div className="text-5xl font-bold text-white tracking-tight">
                    {count}{metric.suffix}
                </div>
            </div>

            <h3 className="text-2xl font-bold text-center mb-4">{metric.label}</h3>
            <p className="text-slate-300 text-center">{metric.sublabel}</p>
        </div>
    );
};

const ServiceMetrics = () => {
    const [activeMetric, setActiveMetric] = useState(0);

    // Auto-advance the active metric every 5 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveMetric((prev) => (prev + 1) % metrics.length);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    return (
        <section className="relative z-10 py-24 pb-32 px-4 bg-[#00101f]">
            {/* Border glow effects */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />

            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <div className="inline-block px-3 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-700/30">
                        Service Excellence
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight max-w-4xl mx-auto">
                        <span className="text-white">Excellence Is Our </span>
                        <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Presence</span>
                    </h2>
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-3xl mx-auto">
                        Delivering reliable solutions with exceptional support
                    </p>
                </div>

                {/* Metrics Cards */}
                <div className="relative max-w-6xl mx-auto">
                    {/* Pipeline connector */}
                    <div className="hidden md:block absolute top-1/2 left-0 right-0 h-6 bg-gradient-to-r from-blue-900/50 via-indigo-900/50 to-blue-900/50 transform -translate-y-1/2 rounded-full border-t border-b border-blue-500/30"></div>

                    {/* Metrics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
                        {metrics.map((metric, index) => (
                            <div key={metric.id} className="relative" onClick={() => setActiveMetric(index)}>
                                {/* Connector to pipeline - vertical line */}
                                <div className="hidden md:block absolute left-1/2 -top-10 w-2 h-10 bg-gradient-to-b from-blue-500/20 to-blue-500/50 transform -translate-x-1/2"></div>

                                {/* Oil valve for active step - using metric color */}
                                {activeMetric === index && (
                                    <div className={`hidden md:block absolute left-1/2 -top-14 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-r ${metric.color} rounded-full border-4 border-slate-900 z-20 ${metric.shadowColor.replace('/20', '/50')}`}></div>
                                )}

                                <ServiceMetricCard metric={metric} active={activeMetric === index} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Decorative oil puddle positioned higher above the bottom border */}
            <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 w-3/4 h-4 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent rounded-full blur-md"></div>
        </section>
    );
};

export default ServiceMetrics;